import {
  Typography,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  styled,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import React, { useState, useMemo, useEffect } from 'react';
import fetchAIPrompts from '../../utils/fetchAIPrompts';
import fetchAIPromptHistory from '../../utils/fetchAIPromptHistory';
import { saveAIPrompt } from '../../utils/saveAIPrompt';
import { promoteToProduction } from '../../utils/promoteToProduction';
import {
  StyledButton,
  StyledOutlinedButton,
} from '../RolesAndPermissions/formComponents/styledComponents';

import '../../settingsStyles.css';

const VersionChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.custom?.secondary || '#35829E',
  color: theme.custom?.textCTA || '#FFFFFF',
  fontWeight: 'bold',
}));

const LatestChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.custom?.primary || '#FF6A00',
  color: theme.custom?.textCTA || '#FFFFFF',
  fontWeight: 'bold',
}));

const ProductionChip = styled(Chip)(() => ({
  backgroundColor: '#4CAF50',
  color: '#FFFFFF',
  fontWeight: 'bold',
}));

const DraftChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.custom?.background3 || '#9E9E9E',
  color: theme.custom?.text || '#FFFFFF',
  fontWeight: 'bold',
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-prompt-tabpanel-${index}`}
      aria-labelledby={`ai-prompt-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function AIPrompts() {
  const [prompts, setPrompts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [askRecorderPrompt, setAskRecorderPrompt] = useState('');
  const [askPaivPrompt, setAskPaivPrompt] = useState('');
  const [originalAskRecorderPrompt, setOriginalAskRecorderPrompt] =
    useState('');
  const [originalAskPaivPrompt, setOriginalAskPaivPrompt] = useState('');
  const [isSaving, setIsSaving] = useState<{
    askRecorder: boolean;
    askPaiv: boolean;
  }>({
    askRecorder: false,
    askPaiv: false,
  });

  // Version history states
  const [askRecorderHistory, setAskRecorderHistory] = useState<any[]>([]);
  const [askPaivHistory, setAskPaivHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState<{
    askRecorder: boolean;
    askPaiv: boolean;
  }>({
    askRecorder: false,
    askPaiv: false,
  });

  // Load initial prompts and version history
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        // @ts-ignore
        const { orgID } = global.me;

        // Load prompts
        const promptsData = await fetchAIPrompts(orgID);
        setPrompts(promptsData);

        // Load version history for all services
        const [askRecorderHistoryData, askPaivHistoryData] = await Promise.all([
          fetchAIPromptHistory(orgID, 'askRecorder'),
          fetchAIPromptHistory(orgID, 'askPaiv'),
        ]);

        setAskRecorderHistory(askRecorderHistoryData);
        setAskPaivHistory(askPaivHistoryData);

        setError(null);
      } catch (err) {
        console.error('Error loading prompts:', err);
        setError('Failed to load prompts');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Find production prompts for each service (fetchAIPrompts already prioritizes production)
  const productionPrompts = useMemo(() => {
    if (!prompts) return { askRecorder: null, askPaiv: null };

    const askRecorder = prompts.find((p: any) => p.service === 'askRecorder');
    const askPaiv = prompts.find((p: any) => p.service === 'askPaiv');

    return { askRecorder, askPaiv };
  }, [prompts]);

  // Update local state when production prompts are loaded
  useEffect(() => {
    if (productionPrompts.askRecorder) {
      const prompt = productionPrompts.askRecorder.prompt || '';
      setAskRecorderPrompt(prompt);
      setOriginalAskRecorderPrompt(prompt);
    }
    if (productionPrompts.askPaiv) {
      const prompt = productionPrompts.askPaiv.prompt || '';
      setAskPaivPrompt(prompt);
      setOriginalAskPaivPrompt(prompt);
    }
  }, [productionPrompts]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleResetPrompt = (service: 'askRecorder' | 'askPaiv') => {
    if (service === 'askRecorder') {
      setAskRecorderPrompt(originalAskRecorderPrompt);
    } else {
      setAskPaivPrompt(originalAskPaivPrompt);
    }
  };

  const hasChanges = (service: 'askRecorder' | 'askPaiv') => {
    if (service === 'askRecorder') {
      return askRecorderPrompt.trim() !== originalAskRecorderPrompt.trim();
    } else {
      return askPaivPrompt.trim() !== originalAskPaivPrompt.trim();
    }
  };

  const handleSavePrompt = async (service: 'askRecorder' | 'askPaiv') => {
    const promptText =
      service === 'askRecorder' ? askRecorderPrompt : askPaivPrompt;
    const serviceName = service === 'askRecorder' ? 'askRecorder' : 'askPaiv';

    if (!promptText.trim()) {
      return;
    }

    setIsSaving((prev) => ({ ...prev, [serviceName]: true }));

    try {
      await saveAIPrompt(service, promptText);
      console.log(`Successfully saved ${service} prompt`);

      // Update original prompt state after successful save
      if (service === 'askRecorder') {
        setOriginalAskRecorderPrompt(promptText);
      } else if (service === 'askPaiv') {
        setOriginalAskPaivPrompt(promptText);
      }

      // Reload prompts after saving
      // @ts-ignore
      const { orgID } = global.me;
      const promptsData = await fetchAIPrompts(orgID);
      setPrompts(promptsData);

      // Refresh history after saving
      loadVersionHistory(service);
    } catch (error) {
      console.error(`Error saving ${service} prompt:`, error);
    } finally {
      setIsSaving((prev) => ({ ...prev, [serviceName]: false }));
    }
  };

  const loadVersionHistory = async (service: 'askRecorder' | 'askPaiv') => {
    const serviceName = service === 'askRecorder' ? 'askRecorder' : 'askPaiv';

    setHistoryLoading((prev) => ({ ...prev, [serviceName]: true }));

    try {
      // @ts-ignore
      const { orgID } = global.me;
      const history = await fetchAIPromptHistory(orgID, service);

      if (service === 'askRecorder') {
        setAskRecorderHistory(history);
      } else {
        setAskPaivHistory(history);
      }
    } catch (error) {
      console.error(`Error loading ${service} history:`, error);
    } finally {
      setHistoryLoading((prev) => ({ ...prev, [serviceName]: false }));
    }
  };

  const handlePromoteToProduction = async (
    promptId: string,
    service: 'askRecorder' | 'askPaiv'
  ) => {
    const serviceName = service === 'askRecorder' ? 'askRecorder' : 'askPaiv';

    setIsSaving((prev) => ({ ...prev, [serviceName]: true }));

    try {
      await promoteToProduction(promptId, service);
      console.log(
        `Successfully promoted prompt ${promptId} to production for ${service}`
      );

      // Reload prompts after promoting
      // @ts-ignore
      const { orgID } = global.me;
      const promptsData = await fetchAIPrompts(orgID);
      setPrompts(promptsData);

      // Refresh history after promoting
      loadVersionHistory(service);
    } catch (error) {
      console.error('Error promoting prompt to production:', error);
    } finally {
      setIsSaving((prev) => ({ ...prev, [serviceName]: false }));
    }
  };

  const VersionHistory = ({
    service,
    history,
    isLoading,
  }: {
    service: string;
    history: any[];
    isLoading: boolean;
  }) => {
    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleString();
    };

    const truncateText = (text: string, maxLength: number = 100) => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };

    if (isLoading) {
      return <CircularProgress size={20} />;
    }

    if (!history || history.length === 0) {
      return (
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.custom?.textLight })}
        >
          No previous versions yet. Save your first prompt to begin tracking
          versions.
        </Typography>
      );
    }

    // Find the latest version
    const latestVersion = history.reduce((latest: any, current: any) =>
      current.revision > latest.revision ? current : latest
    );

    return (
      <List dense>
        {history.map((version: any) => {
          const isLatest = version.revision === latestVersion.revision;
          const isProduction = version.status === 'production';
          const isDraft = version.status === 'draft';

          return (
            <ListItem key={version.id} divider>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <VersionChip
                      label={`v${version.revision.toFixed(1)}`}
                      size="small"
                    />
                    {isProduction && (
                      <ProductionChip label="Production" size="small" />
                    )}
                    {isDraft && <DraftChip label="Draft" size="small" />}
                    {isLatest && !isProduction && (
                      <LatestChip label="Latest" size="small" />
                    )}
                    <Typography
                      variant="body2"
                      sx={(theme) => ({ color: theme.custom?.textLight })}
                    >
                      {formatDate(version.updatedAt)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={(theme) => ({ color: theme.custom?.textLight })}
                    >
                      {truncateText(version.prompt)}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <StyledButton
                  variant={isProduction ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() =>
                    handlePromoteToProduction(
                      version.id,
                      service as 'askRecorder' | 'askPaiv'
                    )
                  }
                  disabled={
                    isProduction || isSaving.askRecorder || isSaving.askPaiv
                  }
                >
                  {isProduction ? 'Production' : 'Set as Production'}
                </StyledButton>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    );
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">Error fetching prompts.</Typography>;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={(theme) => ({
          mb: 3,
          fontWeight: 'bold',
          color: theme.custom?.text,
        })}
      >
        AI Prompts Management
      </Typography>

      <Typography
        variant="body1"
        sx={(theme) => ({
          mb: 3,
          color: theme.custom?.textLight,
        })}
      >
        Configure AI prompts for different services in your organization. These
        prompts will be used to provide context and instructions to AI features.
      </Typography>

      <Box
        sx={(theme) => ({
          borderBottom: 1,
          borderColor: theme.custom?.stroke,
          mb: 2,
        })}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="AI prompt service tabs"
          className="custom-tabs"
          sx={(theme) => ({
            '& .MuiTab-root': {
              color: theme.custom?.textLight,
            },
            '& .Mui-selected': {
              color: `${theme.custom?.primary} !important`,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.custom?.primary,
            },
          })}
        >
          <Tab
            value={0}
            className="custom-tab"
            label="Ask Paiv"
            id="ai-prompt-tab-0"
            aria-controls="ai-prompt-tabpanel-0"
          />
          {/* <Tab
            value={1}
            className="custom-tab"
            label="Ask Paiv"
            id="ai-prompt-tab-1"
            aria-controls="ai-prompt-tabpanel-1"
            disabled={true}
            sx={{
              '&.Mui-disabled': {
                color: 'text.disabled',
                opacity: 0.6,
              },
            }}
          /> */}
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Paper
          sx={(theme) => ({
            p: 3,
            backgroundColor: theme.custom?.background1,
            mb: 2,
            border: `1px solid ${theme.custom?.stroke}`,
          })}
        >
          <Typography
            variant="h6"
            sx={(theme) => ({
              mb: 2,
              fontWeight: 'bold',
              color: theme.custom?.text,
            })}
          >
            Ask Paiv Service
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({
              mb: 3,
              color: theme.custom?.textLight,
            })}
          >
            This prompt will be used to define how AI will access audio
            transcriptions to help coach users or allow them to ask questions
            about specific recordings.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={8}
            label="Ask Recorder Prompt"
            placeholder="Enter the prompt that will be used for Ask Recorder AI processing..."
            value={askRecorderPrompt}
            onChange={(e) => setAskRecorderPrompt(e.target.value)}
            sx={(theme) => ({
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.custom?.background2,
                color: theme.custom?.text,
              },
              '& .MuiInputLabel-root': {
                color: theme.custom?.textLight,
              },
            })}
          />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <StyledOutlinedButton
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={() => handleResetPrompt('askRecorder')}
              disabled={isSaving.askRecorder || !hasChanges('askRecorder')}
            >
              Reset to Original
            </StyledOutlinedButton>

            <StyledButton
              variant="contained"
              onClick={() => handleSavePrompt('askRecorder')}
              disabled={isSaving.askRecorder || !hasChanges('askRecorder')}
            >
              {isSaving.askRecorder ? 'Saving...' : 'Save Prompt'}
            </StyledButton>
          </Box>
        </Paper>

        <Accordion
          sx={(theme) => ({
            backgroundColor: theme.custom?.background1,
            border: `1px solid ${theme.custom?.stroke}`,
            borderRadius: '8px',
            '&:before': { display: 'none' },
          })}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon sx={(theme) => ({ color: theme.custom?.text })} />
            }
          >
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon sx={(theme) => ({ color: theme.custom?.text })} />
              <Typography
                variant="h6"
                sx={(theme) => ({ color: theme.custom?.text })}
              >
                Version History
              </Typography>
              {askRecorderHistory && (
                <Chip
                  label={`${askRecorderHistory.length} versions`}
                  size="small"
                  variant="outlined"
                  sx={(theme) => ({
                    color: theme.custom?.text,
                  })}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <VersionHistory
              service="askRecorder"
              history={askRecorderHistory}
              isLoading={historyLoading.askRecorder}
            />
          </AccordionDetails>
        </Accordion>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Paper
          sx={(theme) => ({
            p: 3,
            backgroundColor: theme.custom?.background1,
            mb: 2,
            border: `1px solid ${theme.custom?.stroke}`,
          })}
        >
          <Typography
            variant="h6"
            sx={(theme) => ({
              mb: 2,
              fontWeight: 'bold',
              color: theme.custom?.text,
            })}
          >
            Ask Paiv Service
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: 'warning.main', fontWeight: 'bold' }}
          >
            ⚠️ This service is currently disabled
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({
              mb: 3,
              color: theme.custom?.textLight,
            })}
          >
            The Ask Paiv service is a general AI feature for asking questions
            about deals. This service is currently disabled as it is not yet
            functional and nothing is accessing it.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={8}
            label="Ask Paiv Prompt"
            placeholder="This service is currently disabled"
            value={askPaivPrompt}
            onChange={(e) => setAskPaivPrompt(e.target.value)}
            disabled={true}
            sx={(theme) => ({
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.custom?.background3,
              },
            })}
          />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <StyledOutlinedButton
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={() => handleResetPrompt('askPaiv')}
              disabled={true}
            >
              Reset to Original
            </StyledOutlinedButton>

            <StyledButton
              variant="contained"
              onClick={() => handleSavePrompt('askPaiv')}
              disabled={true}
            >
              Service Disabled
            </StyledButton>
          </Box>
        </Paper>

        <Accordion
          sx={(theme) => ({
            backgroundColor: theme.custom?.background1,
            border: `1px solid ${theme.custom?.stroke}`,
            borderRadius: '8px',
            '&:before': { display: 'none' },
          })}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon sx={(theme) => ({ color: theme.custom?.text })} />
            }
          >
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon sx={(theme) => ({ color: theme.custom?.text })} />
              <Typography
                variant="h6"
                sx={(theme) => ({ color: theme.custom?.text })}
              >
                Version History
              </Typography>
              {askPaivHistory && (
                <Chip
                  label={`${askPaivHistory.length} versions`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <VersionHistory
              service="askPaiv"
              history={askPaivHistory}
              isLoading={historyLoading.askPaiv}
            />
          </AccordionDetails>
        </Accordion>
      </TabPanel>
    </Box>
  );
}

export default AIPrompts;
